import { CostBasisProvider } from '@/features/cost-basis/context';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './index.scss';
import '@wavvest/wealth-ui/wealth-screen.scss';
import {
  NetWorthDelta,
  ProfileRiskScoreSummary,
  WavvestAccountsTable,
  WealthScreenSkeleton,
  WealthNetWorthCard,
} from '@wavvest/wealth-ui';
import { Tabs, Result, Alert, Button, Space, Tooltip } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { TabsProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { formatCurrency } from '@wavvest/shared-utils';
import { formatAccountType } from '@/util/account-type-format';
import { getInvestorApiReadiness, getInvestorApiRuntimeContext } from '@/api/custom';
import { captureException, captureMessage } from '@/sentry';
import { getClientCorrelationId } from '@/util/metrics';

import { ApexAccount, TabKey } from '@/types/wealth';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useFirmDataEnabled } from '@/hooks/useFirmDataEnabled';
import { useCustodianAccounts } from '@/hooks/useCustodianAccounts';
import { useApexHoldingsAccounts } from '@/hooks/useApexHoldingsAccounts';
import { selectRiaProfile, selectUser } from '@/store/features/userinfo/userinfoSlice';
import {
  selectPlaidLoading,
  selectPlaidHoldings,
  selectPlaidSummary,
  selectPlaidError,
  selectPlaidAccounts,
  selectPlaidChartData,
  selectPlaidHasNoLinkedAccounts,
  selectPlaidSnapshotUnavailable,
} from '@/store/features/plaid/plaid-selectors';

import {
  selectApexAccounts,
  selectApexLoading,
  selectApexChartData,
} from '@/store/features/apex/apex-selectors';
import AccountCollapse from './components/AccountCollapse';
import ChartTab from './components/ChartTab';
import AllocationTab from './components/AllocationTab';
import AllHoldingsTab from './components/AllHoldingsTab';
import { isPlaidInvestmentAccount, selectPlaidDisplayBalance } from '@/util/plaid-util';
import {
  fetchApexAccounts,
  fetchAccountAllocation,
  fetchAccountHoldings,
  fetchApexAssetMap,
  fetchAccountTransfers,
  fetchApexChartData,
} from '@/store/features/apex/apex-thunks';
import { resetApexState } from '@/store/features/apex/apexSlice';
import { fetchPlaidWealthSnapshot, fetchPlaidHoldings } from '@/store/features/plaid/plaid-thunks';
import { resetPlaidState } from '@/store/features/plaid/plaidSlice';
import { TimeRange } from '@/api/custom';
import wavvestLogoIcon from '@/assets/login/wavvest-logo-icon.svg';
import FundingBankChip from './components/FundingBankChip';
import {
  FundingBankReplaceProvider,
  useFundingBankReplace,
} from './components/FundingBankReplaceContext';
import LinkedAccountsSection from './components/LinkedAccountsSection';
import TransferCashModal, { TransferCashTarget } from './components/TransferCashModal';
import { TimeSelectionSegmentedView } from './components/performance-tracker';
import { transferActionDecision, withdrawableAmount } from './components/transfer-button-rules';
import type { TransferActionDecision } from './components/transfer-button-rules';
import { ASSET_LIABILITY } from '@/util/planning-util';
import { wealthCopy } from './wealthCopy';
import { useBankLinkWallets } from '@/hooks/useBankLinkWallets';
import { useAttachAllFundingBanks } from '@/hooks/useAttachAllFundingBanks';
import {
  AttachAllFunding,
  BANK_LINK_PICKER_PATH,
  FundingChip,
  attachAllFundingForWallet,
  findFundingBankAccount,
  formatExternalBankAccountTitle,
  fundingChipForWallet,
  isAttachAllWallet,
} from '@/util/bank-link';
import { FundingBankChipStatic, accountRiskFromScore } from '@wavvest/wealth-ui';
import { useAccountRiskScores } from '@/hooks/useAccountRiskScores';
import { setTransferState } from '@/store/features/transfer/transferSlice';
import { IRA_ACCOUNT_TYPES } from '@/util/transfer-util';
import type { TransactionInfo } from '@/util/transfer-util';

const createInitialPerformanceData = () => ({
  selectedTotal: null,
  lastTotal: 0,
  delta: { value: 0, percentage: 0 },
});

const INVESTOR_READINESS_SIGNAL_INTERVAL = 6;
const WEALTH_READINESS_EXCEPTION_SESSION_KEY = 'wealth_investor_readiness_exception_captured';

const ATTACH_ALL_NO_BANK_DECISION: TransferActionDecision = {
  allowed: false,
  openFundingFirst: false,
  reason: 'Link an external account to move money.',
};

const AccountActions = ({
  account,
  fundingChip,
  attachAll = false,
  fundingLoading = false,
  fundingLoadError = false,
  onDeposit,
  onWithdraw,
}: {
  account: ApexAccount;
  fundingChip?: FundingChip;
  /** Attach-all wallets: an unknown chip (still loading or failed) must not block the old transfer pages. */
  attachAll?: boolean;
  /** bank-link-options state, used to explain a missing chip. */
  fundingLoading?: boolean;
  fundingLoadError?: boolean;
  onDeposit: () => void;
  onWithdraw: () => void;
}) => {
  const { requestOpen } = useFundingBankReplace();
  const availableToWithdraw = withdrawableAmount(account);
  const fundingStatus = fundingChip?.status ?? (attachAll ? 'connected' : 'not_set');
  const attachAllWithoutBank = attachAll && fundingStatus === 'not_set';
  // Without a chip we can't tell "no bank" from "not loaded"; don't offer a
  // transfer that would open a modal it can't submit.
  const unknownFunding: TransferActionDecision | null =
    !attachAll && !fundingChip
      ? {
          allowed: false,
          openFundingFirst: false,
          reason: fundingLoadError
            ? "We couldn't load your funding bank. Try again."
            : fundingLoading
              ? 'Checking your funding bank…'
              : 'A funding bank isn’t available for this account yet.',
        }
      : null;
  const depositDecision = attachAllWithoutBank
    ? ATTACH_ALL_NO_BANK_DECISION
    : (unknownFunding ??
      transferActionDecision({
        mode: 'deposit',
        fundingStatus,
        bankName: fundingChip?.bankName,
        availableToWithdraw,
      }));
  const withdrawDecision = attachAllWithoutBank
    ? ATTACH_ALL_NO_BANK_DECISION
    : (unknownFunding ??
      transferActionDecision({
        mode: 'withdraw',
        fundingStatus,
        bankName: fundingChip?.bankName,
        availableToWithdraw,
      }));

  return (
    <span
      className="wavvest-accounts-actions"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Tooltip title={depositDecision.reason || ''}>
        <button
          type="button"
          className="wavvest-account-action"
          aria-disabled={!depositDecision.allowed}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!depositDecision.allowed) {
              return;
            }
            if (depositDecision.openFundingFirst) {
              requestOpen(account.id);
              return;
            }
            onDeposit();
          }}
        >
          <ArrowDownOutlined aria-hidden />
          Deposit
        </button>
      </Tooltip>
      <Tooltip title={withdrawDecision.reason || ''}>
        <button
          type="button"
          className="wavvest-account-action"
          aria-disabled={!withdrawDecision.allowed}
          onClick={(event) => {
            event.stopPropagation();
            if (withdrawDecision.allowed) {
              onWithdraw();
            }
          }}
        >
          <ArrowUpOutlined aria-hidden />
          Withdraw
        </button>
      </Tooltip>
    </span>
  );
};

const AttachAllFundingChip = ({ funding }: { funding: AttachAllFunding }) => (
  <Tooltip title="Every bank you link is added to this account automatically.">
    <span className="attach-all-funding">
      <FundingBankChipStatic chip={funding.chip} emptyLabel="No linked bank" />
      {funding.otherBankCount > 0 && (
        <span className="wavvest-accounts-funding-text">+{funding.otherBankCount}</span>
      )}
    </span>
  </Tooltip>
);

const Wealth: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const globalRiaProfile = useAppSelector(selectRiaProfile);
  const {
    profileScore: profileRiskScore,
    scoreByAccountId: riskScoreByAccountId,
    loading: riskScoresLoading,
  } = useAccountRiskScores(user?.client_id, user?.cognito_id);
  const shouldIncludeApex =
    Boolean(globalRiaProfile.profile?.apex_correspondent_id) &&
    Boolean(globalRiaProfile.features?.apex_enabled);
  const { enabled: firmDataEnabled, loading: firmDataLoading } = useFirmDataEnabled(
    user?.client_id,
  );
  // Firm-data's blended snapshot (plaidAccounts/plaidSummary, via
  // fetchPlaidWealthSnapshot) already includes this firm's Apex accounts once
  // firm-data is enabled for it. The legacy per-account Apex fetch/render/total
  // pipeline below must stop running in that case, or the same Apex accounts
  // show up twice (once here, once already blended into plaidAccounts) and get
  // double-counted in net worth. This is a data-source-selection concern,
  // distinct from `shouldIncludeApex` (does this firm have Apex at all), which
  // keeps gating purely Apex-availability UI (deposit/withdraw buttons,
  // KYC/account-opening routes) unchanged.
  const shouldFetchLegacyApex = shouldIncludeApex && !firmDataEnabled;

  // Real Apex/custodian account ids for a firm-data + Apex firm, sourced
  // from firm-data's blended plaidAccounts (custodian_account_id) rather
  // than the legacy per-account Apex pipeline above. Used below to fetch
  // holdings for the Asset Allocation / Holdings tabs, which have no other
  // way to reach Apex-custodied holdings once shouldFetchLegacyApex is
  // false. This mirrors, but is independent of, shouldFetchLegacyApex.
  const { accountIds: custodianAccountIds } = useCustodianAccounts();
  const [custodianApexHoldingsRequested, setCustodianApexHoldingsRequested] = useState(false);

  // Accounts shown in the Managed Investments collapse below. Legacy firms
  // get state.apex.accounts exactly as before; firm-data firms get the same
  // accounts rebuilt from firm-data's blended list, keyed by their Apex
  // custodian id so the collapse's own actions (withdraw, holdings,
  // transfers) still reach the right account at Apex. Deliberately not
  // visibleApexAccounts, which is empty for firm-data firms by design — that
  // gate exists to stop the legacy pipeline double-counting accounts, not to
  // hide the accounts themselves.
  const collapseAccounts = useApexHoldingsAccounts();
  const {
    loading: bankLinkLoading,
    pickerWallets,
    attachAllWalletIds,
    pendingSources,
    loadError,
    reload: reloadBankLinkWallets,
  } = useBankLinkWallets(shouldIncludeApex);

  const apexAccounts = useAppSelector(selectApexAccounts);
  const visibleApexAccounts = shouldFetchLegacyApex ? apexAccounts : [];
  const plaidAccounts = useAppSelector(selectPlaidAccounts);
  const fundingChips = useMemo(() => {
    const chips = new Map<string, FundingChip>();
    pickerWallets.forEach((wallet) => {
      chips.set(wallet.account_id, fundingChipForWallet(wallet, plaidAccounts));
    });
    return chips;
  }, [pickerWallets, plaidAccounts]);
  // Firm-data rows have no bank_link_mode of their own; the mode comes from
  // bank-link-options, or from the Apex account list when another page has
  // loaded it (in which case bank-link-options is skipped).
  const attachAllIds = useMemo(
    () =>
      new Set(
        shouldIncludeApex
          ? [
              ...collapseAccounts.filter(isAttachAllWallet).map((account) => account.id),
              ...apexAccounts.filter(isAttachAllWallet).map((account) => account.id),
              ...attachAllWalletIds,
            ]
          : [],
      ),
    [apexAccounts, attachAllWalletIds, collapseAccounts, shouldIncludeApex],
  );
  const isAttachAllAccount = (account: ApexAccount) => attachAllIds.has(account.id);
  const attachAllAccountIds = useMemo(
    () => collapseAccounts.filter((account) => attachAllIds.has(account.id)).map((a) => a.id),
    [attachAllIds, collapseAccounts],
  );
  const attachAllBanks = useAttachAllFundingBanks(attachAllAccountIds);
  const attachAllFunding = useMemo(() => {
    const funding = new Map<string, AttachAllFunding>();
    attachAllBanks.relationships.forEach((relationships, accountId) => {
      funding.set(accountId, attachAllFundingForWallet(accountId, relationships, plaidAccounts));
    });
    return funding;
  }, [attachAllBanks.relationships, plaidAccounts]);
  const plaidSummary = useAppSelector(selectPlaidSummary);
  const plaidChartData = useAppSelector(selectPlaidChartData);
  const plaidError = useAppSelector(selectPlaidError);
  const apexChartData = useAppSelector(selectApexChartData);
  const plaidHasNoLinkedAccounts = useAppSelector(selectPlaidHasNoLinkedAccounts);
  const plaidSnapshotUnavailable = useAppSelector(selectPlaidSnapshotUnavailable);
  const apexLoading = useAppSelector(selectApexLoading);
  const plaidLoading = useAppSelector(selectPlaidLoading);
  const plaidHoldings = useAppSelector(selectPlaidHoldings);

  const [apexDataLoaded, setApexDataLoaded] = useState(false);
  const [plaidSnapshotAttempted, setPlaidSnapshotAttempted] = useState(false);
  const [expandedAccounts, setExpandedAccounts] = useState<string[]>([]);
  const [rightTabKey, setRightTabKey] = useState<TabKey>('portfolio-summary');
  const [timeSelection, setTimeSelection] = useState<TimeRange>('ALL');
  const [transferTarget, setTransferTarget] = useState<TransferCashTarget | null>(null);

  const [performanceData, setPerformanceData] = useState<{
    selectedTotal: number | null;
    lastTotal: number;
    delta: { value: number; percentage: number };
  }>(createInitialPerformanceData());

  const activeUserId = user?.userId || user?.cognito_id || null;
  const activeUserIdRef = useRef<string | null>(activeUserId);
  const apexChartDataFetched = useRef(false);
  const plaidReadinessRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const plaidHoldingsFetchAttemptedRef = useRef(false);
  const [plaidApiReady, setPlaidApiReady] = useState(false);
  const [identityApiReady, setIdentityApiReady] = useState(false);
  const plaidReadinessNotReadyPollCountRef = useRef(0);
  const plaidReadinessExceptionCapturedRef = useRef(false);
  const readinessCorrelationId = useMemo(() => getClientCorrelationId(), []);

  useEffect(() => {
    activeUserIdRef.current = activeUserId;
  }, [activeUserId]);

  useEffect(() => {
    let active = true;

    const clearRetryTimer = () => {
      if (plaidReadinessRetryTimeoutRef.current) {
        clearTimeout(plaidReadinessRetryTimeoutRef.current);
        plaidReadinessRetryTimeoutRef.current = null;
      }
    };

    const checkPlaidReadiness = async () => {
      try {
        const readiness = await getInvestorApiReadiness();
        if (!active) {
          return;
        }
        setPlaidApiReady(readiness.plaidReady);
        setIdentityApiReady(readiness.identityReady);
        if (!readiness.ready) {
          plaidReadinessNotReadyPollCountRef.current += 1;
          const pollCount = plaidReadinessNotReadyPollCountRef.current;
          if (pollCount === 1 || pollCount % INVESTOR_READINESS_SIGNAL_INTERVAL === 0) {
            const runtimeContext = getInvestorApiRuntimeContext();
            captureMessage(
              `[InvestorReadiness][Wealth] route=/home/wealth poll_count=${pollCount} reason=plaidReady:${readiness.plaidReady},identityReady:${readiness.identityReady} client_correlation_id=${readinessCorrelationId} plaid_alias=${runtimeContext.resolvedPlaidAlias || 'none'} identity_alias=${runtimeContext.resolvedIdentityAlias || 'none'} available_aliases=${runtimeContext.availableApiAliases.join('|') || 'none'}`,
              'warning',
            );
          }
          clearRetryTimer();
          plaidReadinessRetryTimeoutRef.current = setTimeout(() => {
            void checkPlaidReadiness();
          }, 5000);
        } else {
          plaidReadinessNotReadyPollCountRef.current = 0;
          clearRetryTimer();
        }
      } catch (error) {
        if (!active) {
          return;
        }
        setPlaidApiReady(false);
        setIdentityApiReady(false);
        plaidReadinessNotReadyPollCountRef.current += 1;
        const pollCount = plaidReadinessNotReadyPollCountRef.current;
        if (
          !plaidReadinessExceptionCapturedRef.current &&
          (() => {
            plaidReadinessExceptionCapturedRef.current = true;
            try {
              if (sessionStorage.getItem(WEALTH_READINESS_EXCEPTION_SESSION_KEY) === '1') {
                return false;
              }
              sessionStorage.setItem(WEALTH_READINESS_EXCEPTION_SESSION_KEY, '1');
            } catch {
              return true;
            }
            return true;
          })()
        ) {
          const runtimeContext = getInvestorApiRuntimeContext();
          const normalizedError = error instanceof Error ? error : new Error(String(error));
          captureException(normalizedError, {
            route: '/home/wealth',
            clientCorrelationId: readinessCorrelationId,
            pollCount,
            reason: 'plaid_readiness_exception',
            plaidAlias: runtimeContext.resolvedPlaidAlias,
            identityAlias: runtimeContext.resolvedIdentityAlias,
            availableApiAliases: runtimeContext.availableApiAliases,
          });
        }
        if (pollCount === 1 || pollCount % INVESTOR_READINESS_SIGNAL_INTERVAL === 0) {
          const runtimeContext = getInvestorApiRuntimeContext();
          captureMessage(
            `[InvestorReadiness][Wealth] route=/home/wealth poll_count=${pollCount} reason=exception client_correlation_id=${readinessCorrelationId} plaid_alias=${runtimeContext.resolvedPlaidAlias || 'none'} identity_alias=${runtimeContext.resolvedIdentityAlias || 'none'} available_aliases=${runtimeContext.availableApiAliases.join('|') || 'none'}`,
            'warning',
          );
        }
        clearRetryTimer();
        plaidReadinessRetryTimeoutRef.current = setTimeout(() => {
          void checkPlaidReadiness();
        }, 5000);
      }
    };

    void checkPlaidReadiness();

    return () => {
      active = false;
      clearRetryTimer();
    };
  }, [readinessCorrelationId]);

  const handleDataUpdate = useCallback(
    (data: {
      selectedTotal: number | null;
      lastTotal: number;
      delta: { value: number; percentage: number };
    }) => {
      setPerformanceData(data);
    },
    [],
  );

  const totalNetWorth = useMemo(() => {
    const apexNetWorth = visibleApexAccounts.reduce(
      (sum: number, account: ApexAccount) => sum + account.total_equity,
      0,
    );
    const plaidNetWorth = plaidSummary?.net_worth || 0;
    return apexNetWorth + plaidNetWorth;
  }, [visibleApexAccounts, plaidSummary]);

  // A single day has no change to report, and performanceData stays at zero
  // whenever the Performance tab is not mounted.
  const hasNetWorthChartData = useMemo(
    () =>
      new Set([
        ...(shouldFetchLegacyApex ? (apexChartData?.timestamps ?? []) : []),
        ...(plaidChartData?.timestamps ?? []),
      ]).size > 1,
    [apexChartData?.timestamps, plaidChartData?.timestamps, shouldFetchLegacyApex],
  );
  const hasPlaidInvestmentAccounts = useMemo(
    () => plaidAccounts.some((account) => isPlaidInvestmentAccount(account)),
    [plaidAccounts],
  );

  const plaidSnapshotNotReady = plaidSnapshotUnavailable;
  const isBrandNewNoExternalAccounts = plaidHasNoLinkedAccounts;
  const hasAnyAccounts = visibleApexAccounts.length + plaidAccounts.length > 0;
  const shouldShowNoAccountsState =
    !hasAnyAccounts && isBrandNewNoExternalAccounts && !plaidSnapshotNotReady;
  const shouldShowPlaidInfoState = plaidSnapshotNotReady && !isBrandNewNoExternalAccounts;
  const investorApiUnavailable = !plaidApiReady || !identityApiReady;
  const shouldShowLimitedServicesWarning =
    investorApiUnavailable ||
    (plaidApiReady && plaidError && !plaidSnapshotNotReady && !isBrandNewNoExternalAccounts);

  useEffect(() => {
    setPerformanceData(createInitialPerformanceData());
    setExpandedAccounts([]);
    setRightTabKey('portfolio-summary');
    setTimeSelection('ALL');
    setApexDataLoaded(false);
    setPlaidSnapshotAttempted(false);
    setCustodianApexHoldingsRequested(false);
    apexChartDataFetched.current = false;

    dispatch(resetApexState());
    dispatch(resetPlaidState());
  }, [dispatch, activeUserId]);

  useEffect(() => {
    setApexDataLoaded(false);
    apexChartDataFetched.current = false;
    dispatch(resetApexState());

    // Wait for the firm-data-enabled flag to resolve before deciding whether
    // to run the legacy Apex fetch at all — firing it while firmDataLoading is
    // true (when firmDataEnabled defaults to false) would briefly re-introduce
    // the duplicate-account/double-count bug this gate exists to prevent.
    if (!activeUserId || !shouldFetchLegacyApex || firmDataLoading) {
      return;
    }

    const requestUserId = activeUserId;
    const apexAccountsRequest = dispatch(fetchApexAccounts());
    const apexAssetMapRequest = dispatch(fetchApexAssetMap());

    const fetchInitialApexChartForUser = async () => {
      try {
        const fetchedApexAccounts = await apexAccountsRequest.unwrap();
        if (activeUserIdRef.current !== requestUserId) {
          return;
        }
        if (!apexChartDataFetched.current) {
          apexChartDataFetched.current = true;
          dispatch(
            fetchApexChartData({
              accountIds: fetchedApexAccounts.map((acc) => acc.id),
              range: 'ALL',
              activeUserId: requestUserId,
            }),
          );
        }
      } catch (error) {
        void error;
      }
    };

    void fetchInitialApexChartForUser();

    return () => {
      apexAccountsRequest.abort();
      apexAssetMapRequest.abort();
    };
  }, [dispatch, activeUserId, shouldFetchLegacyApex, firmDataLoading]);

  // Firm-data + Apex firms never run the legacy per-account fetch above, so
  // the Asset Allocation / Holdings tabs (AllocationTab / AllHoldingsTab)
  // have no other route to real Apex holdings for their custodian-linked
  // accounts. Fetch those by Apex custodian_account_id. When firm-data is
  // on, fetchAccountHoldings reads stored holdings through
  // /api/wealth/holdings (get_holdings_if_enabled) instead of Apex Dynamo.
  useEffect(() => {
    if (
      !activeUserId ||
      shouldFetchLegacyApex ||
      firmDataLoading ||
      custodianAccountIds.length === 0 ||
      custodianApexHoldingsRequested
    ) {
      return;
    }

    setCustodianApexHoldingsRequested(true);
    dispatch(fetchApexAssetMap());
    custodianAccountIds.forEach((accountId) => {
      dispatch(fetchAccountHoldings(accountId));
    });
  }, [
    dispatch,
    activeUserId,
    shouldFetchLegacyApex,
    firmDataLoading,
    custodianAccountIds,
    custodianApexHoldingsRequested,
  ]);

  useEffect(() => {
    if (!activeUserId || !plaidApiReady) {
      return;
    }
    const requestUserId = activeUserId;
    setPlaidSnapshotAttempted(false);
    const plaidSnapshotRequest = dispatch(fetchPlaidWealthSnapshot({ range: 'ALL' }));
    void plaidSnapshotRequest
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        if (activeUserIdRef.current === requestUserId) {
          setPlaidSnapshotAttempted(true);
        }
      });
    return () => {
      plaidSnapshotRequest.abort();
    };
  }, [dispatch, activeUserId, plaidApiReady]);

  // The range picker sits in the net worth card and stays visible on every
  // tab, so the chart for a new range is loaded here rather than by the
  // Performance tab (which unmounts on other tabs). The initial ALL chart comes
  // with the snapshot/Apex loads above; later changes reload both sources.
  const loadedChartRangeRef = useRef<TimeRange>('ALL');
  useEffect(() => {
    loadedChartRangeRef.current = 'ALL';
  }, [activeUserId]);
  useEffect(() => {
    if (!activeUserId || loadedChartRangeRef.current === timeSelection) {
      return;
    }
    loadedChartRangeRef.current = timeSelection;
    if (shouldFetchLegacyApex && apexAccounts.length > 0) {
      dispatch(
        fetchApexChartData({
          accountIds: apexAccounts.map((account) => account.id),
          range: timeSelection,
          activeUserId,
        }),
      );
    }
    if (plaidApiReady) {
      dispatch(
        fetchPlaidWealthSnapshot({
          accountIds: plaidAccounts.map((account) => account.account_id),
          range: timeSelection,
        }),
      );
    }
    // Only a range change should trigger a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSelection, activeUserId]);

  useEffect(() => {
    plaidHoldingsFetchAttemptedRef.current = false;
  }, [rightTabKey]);

  useEffect(() => {
    const needsPlaidHoldings = rightTabKey === 'asset-allocation' || rightTabKey === 'holdings';

    if (rightTabKey === 'portfolio-summary') {
      return;
    }
    if (
      !needsPlaidHoldings ||
      shouldShowNoAccountsState ||
      !plaidApiReady ||
      !activeUserId ||
      !hasPlaidInvestmentAccounts ||
      plaidHoldings ||
      plaidLoading.holdings ||
      plaidHoldingsFetchAttemptedRef.current
    ) {
      return;
    }
    plaidHoldingsFetchAttemptedRef.current = true;
    dispatch(fetchPlaidHoldings({ activeUserId }));
  }, [
    rightTabKey,
    plaidApiReady,
    activeUserId,
    hasPlaidInvestmentAccounts,
    plaidHoldings,
    plaidLoading.holdings,
    shouldShowNoAccountsState,
    dispatch,
  ]);

  useEffect(() => {
    if (
      activeUserId &&
      shouldFetchLegacyApex &&
      visibleApexAccounts.length > 0 &&
      !apexDataLoaded
    ) {
      const apexAccountIds = visibleApexAccounts.map((acc) => acc.id);
      apexAccountIds.forEach((accountId) => {
        dispatch(fetchAccountAllocation({ accountId, activeUserId }));
      });

      setApexDataLoaded(true);
    }
  }, [visibleApexAccounts, dispatch, apexDataLoaded, activeUserId, shouldFetchLegacyApex]);

  const handleTabChange = async (key: string) => {
    const tabKey = key as TabKey;

    if (shouldShowNoAccountsState && tabKey !== 'portfolio-summary') {
      return;
    }

    setRightTabKey(tabKey);

    if (tabKey !== 'portfolio-summary') {
      if (shouldFetchLegacyApex && !apexLoading.holdings) {
        visibleApexAccounts.forEach((account) => {
          if (!account.holdings) {
            dispatch(fetchAccountHoldings(account.id));
          }
        });
      }
    }
  };

  const toggleAccount = (accountId: string) => {
    const opening = !expandedAccounts.includes(accountId);
    setExpandedAccounts(opening ? [accountId] : []);
    if (!opening) {
      return;
    }
    const account = collapseAccounts.find((acc) => acc.id === accountId);
    if (!account) {
      return;
    }
    if (!account.holdings) {
      dispatch(fetchAccountHoldings(accountId));
    }
    if (!account.transferList) {
      dispatch(fetchAccountTransfers(accountId));
    }
  };

  const tabItems: TabsProps['items'] = [
    { key: 'portfolio-summary', label: 'Performance' },
    {
      key: 'asset-allocation',
      label: 'Asset Allocation',
      disabled: shouldShowNoAccountsState,
    },
    {
      key: 'holdings',
      label: 'Holdings',
      disabled: shouldShowNoAccountsState,
    },
  ];

  const hasExternalAccountSnapshot =
    Boolean(plaidSummary) ||
    plaidChartData.timestamps.length > 0 ||
    plaidChartData.values.length > 0;
  const showPreparingExternalAccounts =
    plaidApiReady && !plaidSnapshotAttempted && !hasAnyAccounts && !hasExternalAccountSnapshot;
  const shouldShowInitialWealthLoader =
    !hasAnyAccounts &&
    ((shouldFetchLegacyApex && apexLoading.accounts) ||
      (plaidApiReady && plaidLoading.accounts) ||
      showPreparingExternalAccounts);

  if (shouldShowInitialWealthLoader) {
    return (
      <WealthScreenSkeleton
        message={showPreparingExternalAccounts ? 'Preparing external account data...' : undefined}
      />
    );
  }

  const containerClass = `wealth-container ${theme}-theme`;
  const noAccountsSubtitle = shouldIncludeApex
    ? 'To get started, link an external account below or open an account.'
    : 'To get started, link an external account below.';
  const displayedNetWorth =
    performanceData.selectedTotal ??
    (hasNetWorthChartData ? performanceData.lastTotal : totalNetWorth);
  const wavvestTotal = collapseAccounts.reduce((sum, account) => sum + account.total_equity, 0);
  const externalAccounts = plaidAccounts.filter((account) => account.custodian_code !== 'APEX');
  const cashTotal = externalAccounts
    .filter((account) => account.asset_or_liability !== ASSET_LIABILITY.LIABILITY)
    .reduce((sum, account) => sum + selectPlaidDisplayBalance(account), 0);
  const liabilityTotal = externalAccounts
    .filter((account) => account.asset_or_liability === ASSET_LIABILITY.LIABILITY)
    .reduce((sum, account) => sum + selectPlaidDisplayBalance(account), 0);
  const chartTimestamps = [
    ...(shouldFetchLegacyApex ? apexChartData.timestamps : []),
    ...plaidChartData.timestamps,
  ];
  const openLegacyTransfer = (account: ApexAccount, mode: 'deposit' | 'withdraw') => {
    const transaction: TransactionInfo = {
      kind: mode,
      amount: 0,
      plaidAccountId: '',
      custodianAccountId: account.id,
    };
    dispatch(setTransferState({ state: 'WaitingForFirstAccount', transactions: [transaction] }));
    navigate(mode === 'deposit' ? '/home/deposit' : '/home/withdraw');
  };
  const openTransfer = (account: ApexAccount, mode: 'deposit' | 'withdraw') => {
    // Attach-all wallets and IRAs use the full transfer pages: the modal has no
    // bank picker for attach-all, and no IRA contribution/distribution fields.
    if (isAttachAllAccount(account) || IRA_ACCOUNT_TYPES.has(account.type)) {
      openLegacyTransfer(account, mode);
      return;
    }
    const chip = fundingChips.get(account.id);
    const linkedBank = findFundingBankAccount(plaidAccounts, chip?.plaidId);
    const bankName = chip?.bankName || 'Funding bank';
    const bankLabel =
      chip?.status === 'not_set'
        ? 'No funding bank'
        : linkedBank
          ? formatExternalBankAccountTitle(linkedBank)
          : bankName;
    const bankDetail = chip?.last4
      ? `${chip.accountType || 'Bank'} ••${chip.last4} · Funding bank for ${account.name}`
      : `Funding bank for ${account.name}`;
    setTransferTarget({
      mode,
      accountId: account.id,
      accountName: account.name?.trim() || formatAccountType(account.type) || 'Wavvest account',
      accountTypeLabel: formatAccountType(account.type) || 'Wavvest account',
      fundingChip: chip,
      bankLabel,
      bankDetail,
      bankLogo: chip?.bankLogo,
      availableToWithdraw: withdrawableAmount(account),
    });
  };

  return (
    <FundingBankReplaceProvider
      wallets={pickerWallets}
      pendingSources={pendingSources}
      onUpdated={reloadBankLinkWallets}
    >
      <div className={containerClass}>
        <div className="wealth-profile-risk-row">
          <ProfileRiskScoreSummary score={profileRiskScore} loading={riskScoresLoading} />
        </div>
        <div className="wealth-main-content is-stacked">
          {loadError && (
            <Alert
              className="funding-banks-load-error"
              type="warning"
              showIcon
              message={wealthCopy.fundingBanks.loadFailed.message}
              description={wealthCopy.fundingBanks.loadFailed.description}
              action={
                <Space direction="vertical" size={8}>
                  <Button size="small" onClick={reloadBankLinkWallets}>
                    Try again
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => navigate(BANK_LINK_PICKER_PATH)}
                  >
                    Choose funding bank
                  </Button>
                </Space>
              }
            />
          )}

          <WealthNetWorthCard
            netWorth={formatCurrency(displayedNetWorth)}
            delta={
              hasNetWorthChartData ? (
                <NetWorthDelta
                  value={performanceData.delta.value}
                  percentage={performanceData.delta.percentage}
                  format={formatCurrency}
                />
              ) : null
            }
            breakdown={
              <>
                Wavvest {formatCurrency(wavvestTotal)} · Cash {formatCurrency(cashTotal)}
                {liabilityTotal > 0 ? ` · Liabilities −${formatCurrency(liabilityTotal)}` : ''}
              </>
            }
            timeRange={
              <TimeSelectionSegmentedView
                key={activeUserId ?? 'anonymous'}
                value={timeSelection}
                timestamps={chartTimestamps}
                onChange={setTimeSelection}
              />
            }
            notices={
              <>
                {!investorApiUnavailable &&
                  !isBrandNewNoExternalAccounts &&
                  shouldShowPlaidInfoState && (
                    <Alert
                      style={{ marginBottom: 12 }}
                      type="info"
                      showIcon
                      message={wealthCopy.externalAccounts.preparing.message}
                      description={wealthCopy.externalAccounts.preparing.description}
                    />
                  )}
                {shouldShowLimitedServicesWarning && (
                  <Alert
                    style={{ marginBottom: 12 }}
                    type="warning"
                    showIcon
                    message={wealthCopy.externalAccounts.temporarilyLimited.message}
                    description={wealthCopy.externalAccounts.temporarilyLimited.description}
                  />
                )}
              </>
            }
            tabs={
              <Tabs
                items={tabItems}
                activeKey={rightTabKey}
                onChange={handleTabChange}
                className="wealth-tabs"
              />
            }
            contentClassName={shouldShowNoAccountsState ? 'no-accounts-content' : undefined}
            chart={
              shouldShowNoAccountsState ? (
                <Result
                  icon={<ExclamationCircleOutlined />}
                  title="No accounts linked yet"
                  subTitle={noAccountsSubtitle}
                />
              ) : (
                <>
                  {rightTabKey === 'portfolio-summary' && (
                    <ChartTab
                      onDataUpdate={handleDataUpdate}
                      timeSelection={timeSelection}
                      onTimeSelectionChange={setTimeSelection}
                    />
                  )}
                  {rightTabKey === 'asset-allocation' && <AllocationTab />}
                  {rightTabKey === 'holdings' && <AllHoldingsTab />}
                </>
              )
            }
          />
        </div>

        <WavvestAccountsTable
          total={formatCurrency(wavvestTotal)}
          showActions
          riskLoading={riskScoresLoading}
          rows={collapseAccounts.map((account: ApexAccount) => {
            const nickname = account.name?.trim();
            const accountTypeLabel = formatAccountType(account.type);
            const attachAll = isAttachAllAccount(account);
            const attachAllAccountFunding = attachAll
              ? attachAllFunding.get(account.id)
              : undefined;
            const fundingChip = attachAll
              ? attachAllAccountFunding?.chip
              : fundingChips.get(account.id);
            const funding = attachAllAccountFunding ? (
              <AttachAllFundingChip funding={attachAllAccountFunding} />
            ) : fundingChip && !attachAll ? (
              <FundingBankChip chip={fundingChip} />
            ) : null;
            return {
              id: account.id,
              title: nickname || accountTypeLabel || 'Wavvest account',
              typeLabel: nickname && accountTypeLabel ? accountTypeLabel : undefined,
              risk: accountRiskFromScore(riskScoreByAccountId.get(account.id)),
              balance: formatCurrency(account.total_equity),
              logoSrc: globalRiaProfile?.profile?.logo_mark_uri || wavvestLogoIcon,
              funding,
              actions: (
                <AccountActions
                  account={account}
                  fundingChip={fundingChip}
                  attachAll={attachAll}
                  fundingLoading={bankLinkLoading || apexLoading.accounts}
                  fundingLoadError={loadError}
                  onDeposit={() => openTransfer(account, 'deposit')}
                  onWithdraw={() => openTransfer(account, 'withdraw')}
                />
              ),
              expanded: expandedAccounts.includes(account.id),
              onToggle: () => toggleAccount(account.id),
              details: <AccountCollapse account={account} />,
            };
          })}
        />
        <LinkedAccountsSection wallets={pickerWallets} chartRange={timeSelection} />
        <TransferCashModal
          target={transferTarget}
          onClose={() => setTransferTarget(null)}
          onCompleted={(accountId) => {
            dispatch(fetchAccountTransfers(accountId));
            dispatch(fetchPlaidWealthSnapshot({ range: timeSelection }));
          }}
        />
      </div>
    </FundingBankReplaceProvider>
  );
};

const WealthWithCostBasis = () => (
  <CostBasisProvider>
    <Wealth />
  </CostBasisProvider>
);
export default WealthWithCostBasis;
